import { useEffect, useState } from 'react';

import { api } from '../admin/api';
import '../admin/admin.css';
import {
  AdminWhereContext,
  AliasesPanel,
  MailboxPage,
  MailboxesPanel,
  DirectorySyncPanel,
  AppPasswordsPolicyPanel,
  AppSignInPolicyPanel,
  ArchiveMovePolicyPanel,
  TwoStepPolicyPanel,
  PasswordHelpPanel,
  DisclaimerPanel,
  ForwardingPolicyPanel,
  ScimPanel,
  IdentityProviderPanel,
  TENANT_WHERE,
} from '../admin/mailboxes';
import { MigrationsPanel } from '../admin/migrations';
import { RetentionPanel } from '../admin/retention';
import { DlpPanel } from '../admin/dlp';
import { QuarantinePanel } from '../admin/quarantine';
import { StoragePanel } from '../admin/storage';
import { StorageReport, TrafficReport } from '../admin/reports';
import { AddDomain, DomainPage, TenantAuditPanel } from '../admin/pages';
import { PolicyEditor } from '../admin/spam';
import { Badge, Loaded, useLoad, when } from '../admin/ui';

interface DomainSummary {
  domain: string;
  verified: boolean;
  ready: boolean;
  dns_checked_at: string | null;
  action_needed: string[];
}

/**
 * The tenant's own administration, for members of its admin group: its
 * mailboxes (create, disable, delete, restore, sign out), aliases and groups,
 * provisioning from its identity provider (SCIM), the DNS records each domain
 * needs, the organisation's spam policy, importing mail from an old server, mail
 * retention and legal holds, and data protection rules. The same screens
 * Genworx staff use in the platform console, scoped by the API to this tenant
 * alone; plan, seats and the other tenant settings stay with Genworx.
 */

type Tab =
  | 'mailboxes'
  | 'aliases'
  | 'provisioning'
  | 'imports'
  | 'retention'
  | 'dlp'
  | 'domains'
  | 'spam'
  | 'quarantine'
  | 'files'
  | 'reports'
  | 'activity';

const TABS: Array<[Tab, string]> = [
  ['mailboxes', 'Mailboxes'],
  ['aliases', 'Aliases & groups'],
  ['provisioning', 'Sign-in & provisioning'],
  ['imports', 'Import mail'],
  ['retention', 'Retention & holds'],
  ['dlp', 'Data protection'],
  ['domains', 'Domains & DNS'],
  ['spam', 'Spam policy'],
  ['quarantine', 'Quarantine'],
  ['files', 'Files'],
  ['reports', 'Reports'],
  ['activity', 'Activity log'],
];

/** The mailbox a `#/admin/mailboxes/<address>` hash names, if any. */
function mailboxInHash(): string | null {
  const m = /^#\/admin\/mailboxes\/(.+)$/.exec(window.location.hash);
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}
export function TenantAdmin({
  slug,
  me,
  onBack,
}: {
  slug: string;
  /** The administrator's own address: their test sign-in (ADR-0059). */
  me: string;
  onBack: () => void;
}): JSX.Element {
  const [tab, setTab] = useState<Tab>('mailboxes');
  const [domain, setDomain] = useState<string | null>(null);
  const [mailbox, setMailbox] = useState<string | null>(mailboxInHash);
  const [load, reload] = useLoad(
    (signal) =>
      api<{ domains: DomainSummary[] }>(`/tenants/${encodeURIComponent(slug)}/domains`, { signal }),
    `tenant-domains:${slug}`,
  );

  // The embedded domain page links "back" with a hash; follow it here.
  useEffect(() => {
    const onHash = () => {
      if (window.location.hash === '#/admin/domains') {
        setDomain(null);
      }
      setMailbox(mailboxInHash());
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  return (
    <main className="main adm tenant-admin">
      <div className="adm-head">
        <span className="adm-actions">
          <button type="button" className="btn ghost" onClick={onBack}>
            ← Mail
          </button>
          {TABS.map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={`btn ${tab === id ? '' : 'ghost'}`}
              onClick={() => {
                setTab(id);
                setMailbox(null);
                if (window.location.hash.startsWith('#/admin/')) {
                  window.history.replaceState(null, '', window.location.pathname);
                }
              }}
            >
              {label}
            </button>
          ))}
        </span>
      </div>
      <AdminWhereContext.Provider value={TENANT_WHERE}>
        {tab === 'mailboxes' &&
          (mailbox ? (
            <MailboxPage key={mailbox} slug={slug} email={mailbox} />
          ) : (
            <>
              <MailboxesPanel slug={slug} active />
              <ForwardingPolicyPanel slug={slug} active />
              <AppPasswordsPolicyPanel slug={slug} active />
              <AppSignInPolicyPanel slug={slug} active />
              <ArchiveMovePolicyPanel slug={slug} active />
              <TwoStepPolicyPanel slug={slug} active />
              <PasswordHelpPanel slug={slug} active />
              <DisclaimerPanel slug={slug} active />
            </>
          ))}
        {tab === 'aliases' && <AliasesPanel slug={slug} active />}
        {tab === 'provisioning' && (
          <>
            <IdentityProviderPanel slug={slug} active me={me} />
            <ScimPanel slug={slug} active />
            <DirectorySyncPanel slug={slug} active />
          </>
        )}
        {tab === 'imports' && <MigrationsPanel slug={slug} active />}
        {tab === 'retention' && <RetentionPanel slug={slug} active />}
        {tab === 'dlp' && <DlpPanel slug={slug} active />}
        {tab === 'quarantine' && <QuarantinePanel slug={slug} />}
        {tab === 'files' && <StoragePanel slug={slug} />}
        {tab === 'reports' && (
          <>
            <TrafficReport slug={slug} />
            <StorageReport slug={slug} />
          </>
        )}
        {tab === 'activity' && <TenantAuditPanel slug={slug} />}
      </AdminWhereContext.Provider>
      {tab === 'spam' ? (
        <PolicyEditor
          base={`/tenants/${encodeURIComponent(slug)}/spam-policy`}
          scope="Your organisation's policy"
        />
      ) : tab !== 'domains' ? null : domain ? (
        <DomainPage slug={slug} domain={domain} backHref="#/admin/domains" />
      ) : (
        <section className="adm-panel">
          <h3>Your domains</h3>
          <Loaded load={load}>
            {({ domains }) => (
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>Ownership</th>
                    <th>DNS</th>
                    <th>Last checked</th>
                  </tr>
                </thead>
                <tbody>
                  {domains.map((d) => (
                    <tr key={d.domain}>
                      <td>
                        <button
                          type="button"
                          className="adm-copy"
                          onClick={() => setDomain(d.domain)}
                        >
                          {d.domain}
                        </button>
                      </td>
                      <td>
                        <Badge tone={d.verified ? 'ok' : 'warn'}>
                          {d.verified ? 'verified' : 'unverified'}
                        </Badge>
                      </td>
                      <td>
                        <Badge tone={d.ready ? 'ok' : 'crit'}>
                          {d.ready ? 'ready' : `needs ${d.action_needed.join(', ')}`}
                        </Badge>
                      </td>
                      <td>{when(d.dns_checked_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Loaded>
          <AddDomain slug={slug} onAdded={reload} />
          <p className="adm-muted adm-small">
            Open a domain for the DNS records to publish at its registrar; mail for it works once
            they check out.
          </p>
        </section>
      )}
    </main>
  );
}
